import * as React from 'react';

import * as api from '@gitbook/api-client';
import { emoji, findMentionInDocument, getMentionsFromDocument, mention } from '@gitbook/doc-core';
import { TextRange, type Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonProps,
    type IconDefinition,
    IconPickerButton,
} from '@gitbook/spine-react';

import { triggerEditorMentionPicker } from '../ContentEditor';

/**
 * Button to insert a page mention in the editor.
 */
export function InsertPageMentionButton(props: {
    readOnly?: boolean;
    setValue: React.Dispatch<React.SetStateAction<Value>>;
    size?: ButtonProps['size'];
    leadingIcon?: IconDefinition;
}): React.ReactElement {
    const { setValue, readOnly, size = 'medium', leadingIcon = Icon.PageLink } = props;

    const onPress = () => {
        if (!readOnly) {
            setValue((value) => {
                const change = value.change();
                change.insertText('#');
                triggerEditorMentionPicker(change, '#');
                change.focus();
                return change.value;
            });
        }
    };

    return (
        <Button
            leadingIcon={leadingIcon}
            iconOnly
            size={size}
            kind="blank"
            label="Reference content"
            onPress={onPress}
            tooltip={{
                label: 'Reference content',
                shortcut: '#',
            }}
        />
    );
}

/**
 * Button to reference the GitBook agent in the editor.
 */
export function InsertAgentMentionButton(props: {
    value: Value;
    readOnly?: boolean;
    setValue: React.Dispatch<React.SetStateAction<Value>>;
    size?: ButtonProps['size'];
}): React.ReactElement {
    const { value: initialValue, setValue, readOnly, size = 'medium' } = props;

    const hasAgentMention = React.useMemo(() => {
        const mentions = getMentionsFromDocument(initialValue.document);
        return mentions.some(
            (mention) =>
                mention.ref.kind === 'user' && mention.ref.user === api.GitBookUsers.GitbookAgent
        );
    }, [initialValue.document]);

    const onPress = () => {
        if (!readOnly) {
            setValue((value) => {
                const firstText = value.document.getFirstText();
                if (!firstText) {
                    return value;
                }

                // If the agent is already mentioned, remove it
                if (hasAgentMention) {
                    const agentMentionInline = findMentionInDocument(
                        value.document,
                        (data) =>
                            data.ref.kind === 'user' &&
                            data.ref.user === api.GitBookUsers.GitbookAgent
                    );

                    if (agentMentionInline) {
                        return value.change().removeNodeByKey(agentMentionInline.key).focus().value;
                    }

                    return value;
                }

                // If the agent is not mentioned, insert it
                const agentMentionInline = mention.createMention({
                    kind: 'user',
                    user: api.GitBookUsers.GitbookAgent,
                });
                return value
                    .change()
                    .insertInlineAtRange(
                        TextRange.create({
                            anchorKey: firstText.key,
                            anchorOffset: 0,
                            focusKey: firstText.key,
                            focusOffset: 0,
                            isFocused: true,
                        }),
                        agentMentionInline
                    )
                    .collapseToEndOf(agentMentionInline)
                    .moveToStartOfNextText()
                    .insertText(' ')
                    .focus().value;
            });
        }
    };

    return (
        <Button
            leadingIcon={Icon.AIAgent}
            iconOnly
            size={size}
            kind="blank"
            label="Mention GitBook Agent"
            onPress={onPress}
            active={hasAgentMention}
            tooltip={{
                label: 'Mention GitBook Agent',
                shortcut: '@',
            }}
        />
    );
}

/**
 * Button to insert a user mention in the editor.
 */
export function InsertUserMentionButton(props: {
    readOnly?: boolean;
    setValue: React.Dispatch<React.SetStateAction<Value>>;
    size?: ButtonProps['size'];
}): React.ReactElement {
    const { setValue, readOnly, size = 'medium' } = props;

    const onPress = () => {
        if (!readOnly) {
            setValue((value) => {
                const change = value.change();
                change.insertText('@');
                triggerEditorMentionPicker(change, '@');
                change.focus();
                return change.value;
            });
        }
    };

    return (
        <Button
            leadingIcon={Icon.AtSign}
            iconOnly
            size={size}
            kind="blank"
            label="Mention someone"
            onPress={onPress}
            tooltip={{
                label: 'Mention someone',
                shortcut: '@',
            }}
        />
    );
}

/**
 * Button to insert an emoji
 */
export function InsertEmojiButton(props: {
    readOnly?: boolean;
    setValue: React.Dispatch<React.SetStateAction<Value>>;
    size?: ButtonProps['size'];
}): React.ReactElement {
    const { readOnly, setValue, size = 'medium' } = props;

    const onPickEmoji = (code: string) => {
        if (!readOnly) {
            setValue(
                (value) =>
                    value
                        .change()
                        .insertInlineAtRange(value.textSelection, emoji.createEmoji(code))
                        .focus().value
            );
        }
    };

    return (
        <IconPickerButton
            accept={['emoji']}
            placeholder={Icon.EmojiReaction}
            onPick={onPickEmoji}
            onRemove={() => onPickEmoji('')}
            size={size}
            kind="blank"
            tooltip={{
                label: 'Insert emoji',
                shortcut: ':',
            }}
        />
    );
}
